import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { DownloadDisposition, getDownloadUrl, type Document } from '@/api/queries/storage';

interface DownloadButtonProps {
  selectedDocuments: string[];
  documents: Document[];
  onDownloadComplete?: () => void;
  'data-testid'?: string;
}

export function DownloadButton({
  selectedDocuments,
  documents,
  onDownloadComplete,
  'data-testid': dataTestId,
}: DownloadButtonProps) {
  const { t: tStorage } = useTranslation('storage');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadSelected = async () => {
    if (selectedDocuments.length === 0) return;

    const selectedDocs = documents.filter(doc => selectedDocuments.includes(doc.id));
    setIsDownloading(true);

    try {
      toast.success(tStorage('downloadStarted'));
      for (const document of selectedDocs) {
        // Create a temporary link to trigger download
        const link = window.document.createElement('a');
        link.href = getDownloadUrl(
          document.id,
          document.content_version_id,
          document.name,
          DownloadDisposition.DOWNLOAD
        );
        link.download = document.name;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        // Small delay between downloads to avoid overwhelming the browser
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      onDownloadComplete?.();
    } catch (error) {
      console.error('Error downloading files.', error);
      toast.error(tStorage('errorDownloadingFiles'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleDownloadSelected}
      disabled={selectedDocuments.length === 0 || isDownloading}
      className="text-gray-700 border-gray-300 hover:bg-gray-50"
      data-testid={dataTestId}
    >
      <Download className="h-4 w-4" />
    </Button>
  );
}
